/**
 * Utilitários de formatação para campos de entrada
 */

/**
 * Formata um telefone brasileiro
 * @param phone - Telefone sem formatação
 * @returns Telefone formatado ((xx) xxxxx-xxxx ou (xx) xxxx-xxxx)
 */
export function formatPhone(phone: string): string {
  // ✅ FIX: Proteção contra valores undefined/null
  if (!phone || typeof phone !== 'string') {
    return '';
  }
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length <= 2) {
    return cleanPhone;
  } else if (cleanPhone.length <= 6) {
    return cleanPhone.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  } else if (cleanPhone.length <= 10) {
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  } else {
    // Celular: (xx) xxxxx-xxxx
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
}

/**
 * Formata um CPF brasileiro
 * @param cpf - CPF sem formatação
 * @returns CPF formatado (xxx.xxx.xxx-xx)
 */
export function formatCPF(cpf: string): string {
  // ✅ FIX: Proteção contra valores undefined/null
  if (!cpf || typeof cpf !== 'string') {
    return '';
  }
  
  const cleanCPF = cpf.replace(/\D/g, '');
  
  if (cleanCPF.length <= 3) {
    return cleanCPF;
  } else if (cleanCPF.length <= 6) {
    return cleanCPF.replace(/(\d{3})(\d{0,3})/, '$1.$2');
  } else if (cleanCPF.length <= 9) {
    return cleanCPF.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else {
    return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  }
}

/**
 * Formata um CEP brasileiro
 * @param cep - CEP sem formatação
 * @returns CEP formatado (xxxxx-xxx)
 */
export function formatCEP(cep: string): string {
  // ✅ FIX: Proteção contra valores undefined/null
  if (!cep || typeof cep !== 'string') {
    return '';
  }
  
  const cleanCEP = cep.replace(/\D/g, '');
  
  if (cleanCEP.length <= 5) {
    return cleanCEP;
  } else {
    return cleanCEP.replace(/(\d{5})(\d{0,3})/, '$1-$2');
  }
}

/**
 * Remove formatação de qualquer string, deixando apenas números
 * @param value - String com formatação
 * @returns String apenas com números
 */
export function cleanNumbers(value: string): string {
  // ✅ FIX: Proteção contra valores undefined/null
  if (!value || typeof value !== 'string') {
    return '';
  }
  
  return value.replace(/\D/g, '');
}

/**
 * Valida se um telefone brasileiro tem formato válido
 * @param phone - Telefone formatado ou não
 * @returns true se válido
 */
export function isValidPhone(phone: string): boolean {
  const cleanPhone = cleanNumbers(phone);
  return cleanPhone.length === 10 || cleanPhone.length === 11;
}

/**
 * Valida se um CPF tem 11 dígitos
 * @param cpf - CPF formatado ou não
 * @returns true se tem 11 dígitos
 */
export function isValidCPFLength(cpf: string): boolean {
  const cleanCPF = cleanNumbers(cpf);
  return cleanCPF.length === 11;
}

/**
 * Valida se um CEP tem 8 dígitos
 * @param cep - CEP formatado ou não
 * @returns true se tem 8 dígitos
 */
export function isValidCEPLength(cep: string): boolean {
  const cleanCEP = cleanNumbers(cep);
  return cleanCEP.length === 8;
}

/**
 * Máscaras de entrada para diferentes tipos de campos
 */
export const inputMasks = {
  phone: (value: string) => formatPhone(value),
  cpf: (value: string) => formatCPF(value),
  cep: (value: string) => formatCEP(value),
};

/**
 * Converte uma string para Title Case (primeira letra de cada palavra em maiúscula)
 * @param text - Texto a ser formatado
 * @returns Texto formatado
 */
export function toTitleCase(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
 